import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileCheck2, MessageSquare, XCircle } from "lucide-react";
import { errMsg, fmtMoney, useSSA } from "../SSAContext";
import { AuditTimeline, Card, DemoStamp, ReadinessBadge, StatusBadge, StatusStepper } from "../ui";
import { BriefView } from "../components/AssessmentPanel";
import { PODocument, QuoteOption } from "../components/QuoteOption";

function QuoteDecision({ req, reload }) {
  const { call } = useSSA();
  const [pick, setPick] = useState(1);
  const [msg, setMsg] = useState("");
  const act = async (action) => {
    if (action !== "accept" && !msg.trim()) { toast.error("Add a message first"); return; }
    try { await call("post", `/requests/${req.id}/buyer-decision`, { action, option: pick, message: msg }); toast.success("Decision recorded"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const q = req.quote;
  return (
    <Card eyebrow={`${q.id} · valid until ${q.valid_until} · issued by ${q.entity}`} title="Your quotation" action={<DemoStamp />} testid="buyer-quote-card">
      <p className="text-xs text-slate-500 mb-4">{q.fee_policy}. Provider identities are masked in demo.</p>
      <div className="grid lg:grid-cols-2 gap-4">
        {q.options.map((o) => <QuoteOption key={o.option} o={o} currency={q.currency} selected={pick === o.option} onSelect={req.status === "quote_issued" ? () => setPick(o.option) : null} testid={`buyer-option-${o.option}`} />)}
      </div>
      {req.status === "quote_issued" && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button onClick={() => act("accept")} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-lg" data-testid="accept-option-btn"><FileCheck2 size={15} /> Accept option {pick}</button>
          <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Clarification question or rejection reason" className="flex-1 min-w-[220px] rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="buyer-decision-message" />
          <button onClick={() => act("clarify")} className="inline-flex items-center gap-1 border border-slate-300 px-3 py-2 rounded-lg text-sm" data-testid="request-clarification-btn"><MessageSquare size={14} /> Request clarification</button>
          <button onClick={() => act("reject")} className="inline-flex items-center gap-1 text-rose-600 px-3 py-2 rounded-lg text-sm hover:bg-rose-50" data-testid="reject-both-btn"><XCircle size={14} /> Reject both</button>
        </div>
      )}
    </Card>
  );
}

function BuyerPO({ req, reload }) {
  const { call } = useSSA();
  const [manual, setManual] = useState({ po_number: "", amount: "", currency: req.currency });
  const opt = req.quote.options.find((o) => o.option === req.buyer_decision?.option);
  const submit = async (generate) => {
    try {
      await call("post", `/requests/${req.id}/buyer-po`, generate ? { generate: true } : { generate: false, ...manual, amount: Number(manual.amount) });
      toast.success("Buyer PO validated against accepted quote"); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card eyebrow={`Accepted option ${opt.option} · ${fmtMoney(opt.total, req.currency)}`} title="Issue your PO to the Infosys Aggregator entity" testid="buyer-po-card">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="text-sm font-semibold">Generate synthetic demo PO</div>
          <p className="text-xs text-slate-600 mt-1">Creates a PO labelled “DEMO – NOT A VALID PURCHASE ORDER” for exactly the accepted total.</p>
          <button onClick={() => submit(true)} className="mt-3 bg-[#0B2A4A] text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="generate-buyer-po-btn">Generate demo PO</button>
        </div>
        <div className="rounded-lg border border-slate-200 p-4 space-y-2">
          <div className="text-sm font-semibold">Enter your own PO reference</div>
          <input placeholder="PO number" value={manual.po_number} onChange={(e) => setManual({ ...manual, po_number: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" data-testid="manual-po-number" />
          <div className="flex gap-2">
            <input type="number" placeholder="PO value" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} className="flex-1 rounded border border-slate-200 px-2 py-1.5 text-sm" data-testid="manual-po-amount" />
            <select value={manual.currency} onChange={(e) => setManual({ ...manual, currency: e.target.value })} className="rounded border border-slate-200 px-2 text-sm" data-testid="manual-po-currency">
              {["EUR", "GBP", "USD", "INR"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <button onClick={() => submit(false)} className="border border-slate-300 text-sm font-semibold px-4 py-2 rounded-lg" data-testid="submit-manual-po-btn">Validate & submit PO</button>
          <p className="text-[11px] text-slate-500">Value and currency must match the accepted quote (tolerance 0.00 per policy).</p>
        </div>
      </div>
    </Card>
  );
}

export default function BuyerRequestPage() {
  const { rid } = useParams();
  const { call, refreshKey, setActiveRid } = useSSA();
  const [req, setReq] = useState(null);
  const reload = useCallback(() => call("get", `/requests/${rid}`).then(setReq).catch((e) => toast.error(errMsg(e))), [call, rid]);
  useEffect(() => { setActiveRid(rid); reload(); }, [rid, refreshKey, reload, setActiveRid]);
  if (!req) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>;
  const waiting = ["submitted", "procurement_review", "rfq_issued", "partner_clarification", "responses_received", "evaluation", "shortlisted"].includes(req.status);
  return (
    <div className="space-y-5" data-testid="buyer-request-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <div className="font-mono text-sm text-slate-500" data-testid="buyer-request-ref">{req.id}</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900">{req.service_name}</h1>
          <div className="text-sm text-slate-500 mt-1">{req.project_label} · {req.country} · {req.currency}</div>
        </div>
        <ReadinessBadge value={req.readiness} /><StatusBadge status={req.status} />
        {req.is_demo && <DemoStamp />}
      </div>
      <Card><StatusStepper status={req.status} /></Card>
      {waiting && <Card testid="buyer-waiting"><p className="text-sm text-slate-600">Infosys Procurement is validating your brief and running a private, competitive partner process. You will see up to two comparable options here — competing prices, partner identities and internal evaluation are never shown.</p></Card>}
      {req.status === "returned_to_buyer" && <Card testid="buyer-returned"><p className="text-sm text-rose-700">Procurement returned this request for clarification. Please submit a revised brief from the service page.</p></Card>}
      {req.quote && <QuoteDecision req={req} reload={reload} />}
      {req.status === "buyer_clarification" && <Card testid="buyer-clarification-pending"><p className="text-sm">Clarification sent: “{req.buyer_decision?.clarification}”. Procurement will issue a revised quote.</p></Card>}
      {req.status === "buyer_accepted" && <BuyerPO req={req} reload={reload} />}
      {req.buyer_po && <PODocument po={req.buyer_po} kind="buyer" testid="buyer-po-document" />}
      {req.status === "infosys_po_issued" && <Card testid="buyer-po-chain-complete"><p className="text-sm text-emerald-700 font-semibold">Infosys has issued a back-to-back PO to the selected provider. Delivery kick-off follows.</p></Card>}
      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Your brief" eyebrow="As validated">{req.brief ? <BriefView brief={req.brief} /> : "—"}</Card>
        <Card title="Audit timeline" eyebrow="Every action logged"><AuditTimeline rid={req.id} perspective="buyer" /></Card>
      </div>
    </div>
  );
}
